import Badge from "@/src/components/atom/Badge";
import Typo from "@/src/components/atom/Typo";
import BreakWordTypo from "@/src/components/molecule/BreakWordTypo";
import Flex from "@/src/components/molecule/Flex";
import SectionCardGrid from "@/src/components/molecule/SectionCardGrid";
import SectionCardRow from "@/src/components/molecule/SectionCardRow";
import DATE_FORMAT_STRINGS from "@/src/constant/dateFormat";
import {
  getFormattedTimezoneDateWithOffset,
  getTimezoneDateWithOffset,
} from "@/src/hooks/useCompanyLocalDate";
import { useAppSelector } from "@/src/store";
import { subscriptionApi } from "@/src/store/apis/subscription";
import colorSet from "@/src/styles/color";
import { getPlanServiceCountColor } from "@/src/utils/color";
import { isNull, isUndefined } from "@/src/utils/is";
import { calculateRemainingDays } from "@/src/utils/subscriptionDate";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { parseAmount, isFreeAmount } from "@/src/utils/subscription";

function SubscriptionInfo() {
  const { t } = useTranslation();
  const userSession = useAppSelector((state) => state.auth.user);
  const gmtOffset = userSession?.exporter.gmtOffset || 0;

  const { subscriptionId } =
    subscriptionApi.endpoints.getSubscriptions.useQueryState(
      { page: 1, pageSize: 10 },
      {
        selectFromResult: ({ currentData }) => {
          return {
            subscriptionId: currentData?.rows[0].id,
          };
        },
      }
    );

  const {
    planName,
    paymentCycle,
    subscriptionStartAtFrom,
    subscriptionEndAtTo,
    annualFee,
    monthlyFee,
    currentMemberNumberCredit,
    planMemberNumberCredit,
    subscriptionStatus,
    subscriptionType,
    nextPlan,
    isError,
    isFetching,
    isStable,
    subscriptionNo,
    annualPaymentInstallment,
    availableCredit,
    totalCredit,
  } = subscriptionApi.endpoints.getSubscription.useQueryState(
    { id: subscriptionId as number },
    {
      selectFromResult: ({ currentData, isError, isFetching }) => {
        const isUnStable = isError || isFetching || isUndefined(currentData);
        const isStable = !isUnStable;

        return {
          currentData,
          isError,
          isFetching,
          isStable,
          subscriptionNo: currentData?.subscriptionNo,
          subscriptionType: currentData?.subscriptionType,
          annualPaymentInstallment: currentData?.annualPaymentInstallment,
          nextPlan: currentData?.nextPlan,
          subscriptionStatus: currentData?.subscriptionStatus,
          planName: isStable ? currentData.plan.name : "-",
          paymentCycle: currentData?.paymentCycle,
          annualFee: isStable ? (currentData.planVersion.annualFee ?? "") : "",
          monthlyFee: isStable
            ? (currentData.planVersion.monthlyFee ?? "")
            : "",
          subscriptionStartAtFrom: currentData?.subscriptionStartAtFrom,
          subscriptionEndAtTo: currentData?.subscriptionEndAtTo,
          currentMemberNumberCredit: isStable
            ? currentData.memberNumberCredit
            : 0,
          planMemberNumberCredit: isStable
            ? (currentData.planVersion.numberOfMember ?? 0)
            : 0,
          availableCredit: isStable
            ? currentData.credit?.availableCredit
            : undefined,
          totalCredit: isStable ? currentData.credit?.totalCredit : undefined,
        };
      },
    }
  );

  const { firstAnnualSubscription, isFirstAnnualSubscriptionFetching } =
    subscriptionApi.endpoints.getSubscriptions.useQueryState(
      { annualPaymentInstallment: 1, subscriptionNo },
      {
        selectFromResult: ({ currentData, isFetching }) => {
          return {
            firstAnnualSubscription: currentData?.rows[0],
            currentData,
            isFirstAnnualSubscriptionFetching: isFetching,
          };
        },
      }
    );

  const duration = subscriptionEndAtTo ? calculateRemainingDays(subscriptionEndAtTo) : 0;
  // 만료 여부 체크 (시간, 분, 초까지 정확히 비교)
  const isExpired = subscriptionEndAtTo
    ? dayjs().isAfter(dayjs(subscriptionEndAtTo))
    : true;
  const isPlanTermination = subscriptionStatus === "CANCELED" && !isExpired;
  const subscriptionRenewalDate = subscriptionEndAtTo
    ? getTimezoneDateWithOffset({
        date: subscriptionEndAtTo,
        gmtOffset,
      })
        .add(1, "day")
        .format(DATE_FORMAT_STRINGS.YYYY_MM_DD)
    : "-";

  const getSubscriptionStartDate = () => {
    switch (paymentCycle) {
      case "ANNUALLY":
        if (
          firstAnnualSubscription?.subscriptionStartAtFrom &&
          annualPaymentInstallment
        ) {
          return getFormattedTimezoneDateWithOffset({
            date: firstAnnualSubscription.subscriptionStartAtFrom,
            gmtOffset,
          });
        }
        return "";
      case "MONTHLY":
      case null:
        if (subscriptionStartAtFrom) {
          return getFormattedTimezoneDateWithOffset({
            date: subscriptionStartAtFrom,
            gmtOffset,
          });
        }
        return "";
    }
  };
  const getSubscriptionEndDate = () => {
    switch (paymentCycle) {
      case "ANNUALLY":
        if (subscriptionEndAtTo && annualPaymentInstallment) {
          const remainingMonths = 12 - annualPaymentInstallment;
          return getTimezoneDateWithOffset({
            date: subscriptionEndAtTo,
            gmtOffset,
          })
            .add(remainingMonths, "month")
            .format(DATE_FORMAT_STRINGS.YYYY_MM_DD);
        }
        return "";
      case "MONTHLY":
      case null:
        if (subscriptionEndAtTo) {
          return getFormattedTimezoneDateWithOffset({
            date: subscriptionEndAtTo,
            gmtOffset,
          });
        }
        return "";
    }
  };

  const subscriptionDate = `${getSubscriptionStartDate()}–${getSubscriptionEndDate()}`;

  const renderBadge = () => {
    const isFreePlan =
      subscriptionStatus === "PROGRESS" && subscriptionType === "FREE";
    const isStandardPlan = !isFreePlan;
    const isUnstableNextPlan = isNull(nextPlan);
    const isNextPlan = !isUnstableNextPlan;

    if (isError || isFetching || isFirstAnnualSubscriptionFetching) {
      return null;
    }

    if (isNextPlan) {
      return (
        <Badge
          text={t("subscriptionManagement:subscriptionStatus.changePlan")}
          color="systemBlue2"
          badgeColor="white"
          borderColor="systemBlue2"
          badgeSize="S"
          badgeType="line"
        />
      );
    }

    if (isPlanTermination) {
      return (
        <Badge
          text={t(
            "subscriptionManagement:subscriptionStatus.subscriptionTerminated"
          )}
          color="red2"
          badgeColor="white"
          borderColor="red2"
          badgeSize="S"
          badgeType="line"
        />
      );
    }

    if (isStandardPlan) {
      const paymentCycleText =
        paymentCycle === "MONTHLY"
          ? t("subscriptionManagement:subscription.monthlySubscription")
          : t("subscriptionManagement:subscription.annualSubscription");

      return (
        <Badge
          text={paymentCycleText}
          color="systemLime1"
          badgeColor="white"
          borderColor="systemLime1"
          badgeSize="S"
          badgeType="line"
        />
      );
    }

    return <></>;
  };

  const renderPlanName = () => {
    // 구독 타입 Free인 경우
    if (subscriptionType === "FREE") {
      return (
        <StyledSectionGridCardRow
          label={
            <Flex gap={4} alignItems="center">
              <Typo color="gray6" typoType="b9m">
                {t("subscriptionManagement:subscription.plan")}
              </Typo>
              {renderBadge()}
            </Flex>
          }
          direction="vertical"
          value={
            <>
              <BreakWordTypo typoType="h4">{planName}</BreakWordTypo>{" "}
              <BreakWordTypo typoType="h4" color="blue4">
                ({duration} {t("subscriptionManagement:subscription.daysLeft")})
              </BreakWordTypo>
            </>
          }
        />
      );

      // 구독 타입 STANDARD
    } else {
      return (
        <StyledSectionGridCardRow
          label={
            <Flex gap={4} alignItems="center">
              <Typo color="gray6" typoType="b9m">
                {t("subscriptionManagement:subscription.plan")}
              </Typo>{" "}
              {renderBadge()}
            </Flex>
          }
          direction="vertical"
          value={<BreakWordTypo typoType="h4">{planName}</BreakWordTypo>}
        />
      );
    }
  };

  const freePlanLabel = t("subscriptionManagement:subscriptionStatus.free");

  const renderCurrentPrice = () => {
    if (subscriptionType === "FREE") {
      return freePlanLabel;
    }

    if (paymentCycle === "ANNUALLY") {
      if (isFreeAmount(annualFee)) {
        return freePlanLabel;
      }

      const annualValue = parseAmount(annualFee);

      if (!Number.isNaN(annualValue)) {
        return `${annualValue.toLocaleString("ko-KR")} USD`;
      }

      return freePlanLabel;
    }

    if (paymentCycle === "MONTHLY") {
      if (isFreeAmount(monthlyFee)) {
        return freePlanLabel;
      }

      const monthlyValue = parseAmount(monthlyFee);

      if (!Number.isNaN(monthlyValue)) {
        return `${monthlyValue.toLocaleString("ko-KR")} USD`;
      }

      return freePlanLabel;
    }

    return freePlanLabel;
  };

  const renderSectionCardGrid = () => {
    // 구독이 없는 상태 (시간까지 정확히 체크)
    if (
      subscriptionStatus === "FINISHED" ||
      (isExpired && subscriptionStatus && ["CANCELED", "PAYMENT_FAIL"].includes(subscriptionStatus))
    ) {
      return <></>;
    }

    if (isStable) {
      return (
        <SectionCardGrid>
          {renderPlanName()}
          <StyledSectionGridCardRow
            label={
              <Typo color="gray6" typoType="b9m">
                {t("subscriptionManagement:subscription.subscriptionDateAt")}
              </Typo>
            }
            direction="vertical"
            value={
              <BreakWordTypo typoType="h4">{subscriptionDate}</BreakWordTypo>
            }
          />

          <StyledSectionGridCardRow
            label={
              <Typo color="gray6" typoType="b9m">
                {t("subscriptionManagement:subscription.subscriptionPrice")}
              </Typo>
            }
            direction="vertical"
            value={
              <BreakWordTypo typoType="h4">
                {renderCurrentPrice()}
              </BreakWordTypo>
            }
          />
          <Divider />

          <StyledSectionGridCardRow
            label={
              <Typo color="gray6" typoType="b9m">
                {/* 월간 잔여 Export Credit */}
                {t(
                  "subscriptionManagement:subscription.remainingLoadingCredits"
                )}
              </Typo>
            }
            direction="vertical"
            value={
              <>
                <BreakWordTypo
                  typoType="h4"
                  color={getPlanServiceCountColor({
                    type: "loading",
                    currentCount: availableCredit ?? 0,
                    planCount: totalCredit ?? 0,
                  })}
                >
                  {availableCredit ?? 0}
                </BreakWordTypo>{" "}
                <BreakWordTypo typoType="h4">
                  / {totalCredit ?? 0}
                </BreakWordTypo>
              </>
            }
          />
          <StyledSectionGridCardRow
            label={
              <Typo color="gray6" typoType="b9m">
                {t("subscriptionManagement:subscription.numberOfUsersMonth")}
              </Typo>
            }
            direction="vertical"
            value={
              <>
                <BreakWordTypo
                  typoType="h4"
                  color={getPlanServiceCountColor({
                    type: "member",
                    currentCount:
                      planMemberNumberCredit - currentMemberNumberCredit,
                    planCount: planMemberNumberCredit,
                  })}
                >
                  {planMemberNumberCredit - currentMemberNumberCredit}
                </BreakWordTypo>{" "}
                <BreakWordTypo typoType="h4">
                  / {planMemberNumberCredit}
                </BreakWordTypo>
              </>
            }
          />
          <StyledSectionGridCardRow
            label={
              <Typo color="gray6" typoType="b9m">
                {t("subscriptionManagement:subscription.RenewNextPayment")}
              </Typo>
            }
            direction="vertical"
            value={
              <Flex gap={4} alignItems="center">
                <BreakWordTypo typoType="h4">
                  {isPlanTermination ? "-" : subscriptionRenewalDate}
                </BreakWordTypo>
              </Flex>
            }
          />
        </SectionCardGrid>
      );
    }

    return <></>;
  };

  return renderSectionCardGrid();
}

export default SubscriptionInfo;

const StyledSectionGridCardRow = styled(SectionCardRow)`
  grid-column: 4 span;

  .section-label {
    max-width: 100%;
  }
  .section-row-single-value {
    max-width: 100%;
  }
`;

const Divider = styled.div`
  grid-column: 12 span;
  border-bottom: 1px solid ${colorSet.gray9};
`;
